<template>
  <label
    class="flex gap-3 py-1.5 transition"
    :class="[isDisabled && `opacity-50`]"
  >
    <SwitchRoot
      :id="fieldContext?.inputId"
      ref="input"
      v-model="modelValue"
      :disabled="isDisabled"
      :aria-label="label"
      :aria-invalid="fieldContext?.validationStatus.value === 'error'"
      :aria-describedby="ariaDescribedBy"
      :class="[
        `flex h-5 w-9 shrink-0 items-center overflow-hidden rounded-full border border-border outline-2 outline-offset-2 outline-blue-500 transition-colors duration-200 ease-in-out focus-visible:outline disabled:cursor-not-allowed`,
        modelValue ? `bg-blue-500 hover:bg-blue-600` : `bg-card hover:bg-muted`,
      ]"
      @focus="onFocus"
      @blur="onBlur"
    >
      <SwitchThumb
        class="m-1 ml-[0.1875rem] size-3 rounded-full bg-white shadow transition duration-200 ease-in-out data-[state=checked]:translate-x-4"
      />
    </SwitchRoot>

    <span v-if="slots.default" class="text-sm">
      <slot />
    </span>
  </label>
</template>

<script setup lang="ts">
import { SwitchRoot, SwitchThumb } from "reka-ui";
import { computed, onMounted, useTemplateRef } from "vue";

import { injectFieldContext } from "./context";

const modelValue = defineModel<boolean>({ default: false });

const slots = defineSlots<{
  default?(): unknown;
}>();

const props = defineProps<{
  label?: string;
  disabled?: boolean;
  autofocus?: boolean;
  onFocus?: (event: FocusEvent) => void;
  onBlur?: (event: FocusEvent) => void;
}>();

const input = useTemplateRef("input");

const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

onMounted(() => {
  if (props.autofocus) {
    input.value?.$el.focus({ preventScroll: true });
  }
});

defineExpose({
  input: computed(() => {
    return input.value?.$el as HTMLButtonElement | null | undefined;
  }),
});
</script>
